import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Input,
  InputNumber,
  Modal,
  Select,
  Tag,
  Typography,
} from "antd";
import { useTranslation } from "react-i18next";
import moment from "moment";
import axios, { AxiosError, AxiosResponse } from "axios";
import api from "../../../services/api";
import { IActionModalProps } from "..";
import { useToast } from "../../../hooks/toast";
import {
  ContainerFlex,
  EstimationLine,
  TotalHours,
  TotalHoursContainer,
} from "./styles";
import ProjectTypes from "../../../enums/ProjectTypes";
import filterOptions from "../../../utils/filterOptions";
import ITypePricingProject from "../../../interfaces/ITypePricingProject";
import { Flex } from "../ProposalModal/styles";

const { Text } = Typography;

interface IOption {
  label: string;
  value: string;
}

interface IActivityFormData {
  identifier: string;
  description: string;
  hours?: number;
  type: string;
  consultant_id?: string;
  project_id: string;
  allocation_month: moment.Moment;
}

interface IEstimation {
  identifier: string;
  description: string;
  requirement_id: string;
  hours?: number;
  description_hour_factory?: string;
  value_hour_factory?: number;
  hour_factory_id?: string;
}

type IHoursFactoryUpdate = { activity_id: string; hours_factory_id: string };

const EstimateModal: React.FC<IActionModalProps> = ({
  project,
  handleUpdateProject,
}) => {
  const { t } = useTranslation(["project-actions"]);
  const [visible, setVisible] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [estimations, setEstimations] = useState<IEstimation[]>([]);
  const [hoursFactory, setHoursFactory] = useState<
    { description: string; id: string }[]
  >();
  const [selectedHourFactory, setSelectedHourFactory] = useState<
    IHoursFactoryUpdate[]
  >([]);

  const { addToast } = useToast();

  const handleClose = useCallback(() => {
    setVisible(false);
  }, []);

  useEffect(() => {
    if (project.pricing === ITypePricingProject.HoursSeniority) {
      api
        .get<{ description: string; id: string }[]>("config_hours_allocation")
        .then(resp => setHoursFactory(resp.data));
    }
  }, [project.pricing]);

  useEffect(() => {
    setEstimations(
      project.requirements.map(req => {
        const date = new Date(req.year, req.month);
        const dateString = moment(date).format("MMMM [de] YYYY");
        return {
          identifier: req.identifier,
          description: req.description || dateString,
          requirement_id: req.id,
          hours: req.hours,
          description_hour_factory: req?.hoursFactory?.configHour?.description,
          value_hour_factory: req?.hoursFactory?.configHour?.value_hour,
          hour_factory_id: req?.hoursFactory?.configHour?.id,
        };
      }),
    );
  }, [project.requirements, project]);

  const handleEstimationChange = useCallback(
    (req: IEstimation, value: number | string) => {
      setEstimations(oldEstimations => {
        const newEstimations = oldEstimations;
        const findIndex = newEstimations.findIndex(
          est => est.requirement_id === req.requirement_id,
        );
        if (findIndex > -1) {
          newEstimations[findIndex].hours =
            value !== null && value !== undefined ? Number(value) : undefined;
        }
        return [...newEstimations];
      });
    },
    [],
  );

  const totalHours = useMemo(
    () => estimations.reduce((acc, curr) => acc + (curr.hours || 0), 0),
    [estimations],
  );

  const validatedEstimations = useMemo(() => {
    if (project.projectType.acronym !== ProjectTypes.Factory) {
      return true;
    }
    return project.requirements.every(est => est?.hours && est?.estimator_id);
  }, [project]);

  const generateErrorToast = useCallback(
    (response: AxiosResponse | undefined) => {
      let description;

      switch (response?.status) {
        case 405:
          description =
            "O projeto tem que estar na fase de entendimento para ser estimado";
          break;
        case 406:
          description =
            "O projeto precisa estar com as estimativas confirmadas";
          break;
        case 404:
          description = "O projeto não foi encontrado";
          break;
        default:
          description = "Um erro occoreu ao estimar o projeto, tente novamente";
      }

      addToast({
        type: "error",
        title: "Erro ao estimar",
        description,
      });
    },
    [addToast],
  );

  const handleSubmit = useCallback(async () => {
    setLoading(true);
    const missingEstimation = estimations.some(
      est => est.hours === null || est.hours === undefined,
    );

    if (missingEstimation) {
      addToast({
        type: "error",
        title: "Erro na estimativa",
        description:
          "Você precisa preencher a estimativa de todos os requisitos para validar a estimativa. Obs: o requisito pode ter uma estimativa de 0h",
      });
    } else {
      const payload = {
        estimations: estimations.map(({ requirement_id, hours }) => ({
          requirement_id,
          hours,
        })),
      };
      try {
        const response = await api.patch(
          `/projects/${project.id}/estimate`,
          payload,
        );

        handleUpdateProject(response.data);
        const projectHourSeniorityAndExistHoursFactory =
          project.pricing === ITypePricingProject.HoursSeniority &&
          selectedHourFactory.length > 0;

        if (projectHourSeniorityAndExistHoursFactory) {
          const promises: any[] = [];
          selectedHourFactory.forEach(({ activity_id, hours_factory_id }) => {
            promises.push(
              api.patch(`/activities/${activity_id}/hours_factory_update`, {
                hours_factory_id,
              }),
            );
          });
          await Promise.all(promises);
        }
        addToast({
          type: "success",
          title: "Projeto estimado",
          description: "O projeto foi estimado com sucesso",
        });
      } catch (err) {
        if (axios.isAxiosError(err)) {
          const serverError = err as AxiosError;
          generateErrorToast(serverError.response);
        } else {
          console.log(err);
        }
      }
    }
    setLoading(false);
  }, [
    addToast,
    estimations,
    generateErrorToast,
    handleUpdateProject,
    project.id,
    selectedHourFactory,
  ]);

  const warning = useCallback(() => {
    Modal.warning({
      title: "Nenhuma atividade",
      content:
        "O projeto tem que ter pelo menos uma atividade para ser estimado.",
    });
  }, []);

  const handleShowModal = useCallback(() => {
    if (estimations.length > 0 || project.public_project) {
      setVisible(true);
    } else {
      warning();
    }
  }, [estimations.length, warning, project]);

  const optionsHoursFactory = useMemo(() => {
    return hoursFactory?.map(hour => ({
      label: hour.description,
      value: hour.id,
    }));
  }, [hoursFactory]);

  const handleUpdateHourFactory = useCallback(
    ({ hours_factory_id, activity_id }: IHoursFactoryUpdate) => {
      setSelectedHourFactory(oldSelected => {
        const findIndex = oldSelected.findIndex(
          sel => sel.activity_id === activity_id,
        );
        if (findIndex > -1 && !hours_factory_id) {
          oldSelected.splice(findIndex, 1);
          return [...oldSelected];
        }
        if (findIndex > -1) {
          // eslint-disable-next-line no-param-reassign
          oldSelected[findIndex] = { activity_id, hours_factory_id };
        } else {
          oldSelected.push({ activity_id, hours_factory_id });
        }
        return [...oldSelected];
      });
    },
    [],
  );

  const ProjectHourSeniority = useMemo(() => {
    return project?.pricing === ITypePricingProject.HoursSeniority;
  }, [project]);

  return (
    <>
      <Modal
        title={t("estimate")}
        visible={visible}
        width={ProjectHourSeniority ? 800 : 600}
        onOk={handleSubmit}
        onCancel={() => handleClose()}
        okButtonProps={{
          htmlType: "submit",
          loading,
          disabled: !validatedEstimations,
        }}
        getContainer={false}
        destroyOnClose
      >
        {estimations.map(est => {
          return (
            <EstimationLine key={est.requirement_id}>
              <ContainerFlex>
                <Flex style={{ alignItems: "center" }}>
                  <InputNumber
                    onChange={value => handleEstimationChange(est, value)}
                    defaultValue={est.hours}
                    style={{ minWidth: 100, maxWidth: 100 }}
                    status={
                      est.hours === null || est.hours === undefined
                        ? "error"
                        : undefined
                    }
                    addonAfter="h"
                    min={0}
                  />
                  <Text ellipsis style={{ marginLeft: 10 }}>
                    {est.identifier} - {est.description}
                  </Text>
                </Flex>
                <Flex style={{ alignItems: "flex-start", width: "100%" }}>
                  {project?.pricing &&
                    project.pricing === ITypePricingProject.HoursSeniority && (
                      <>
                        <Select
                          allowClear={!est.hour_factory_id}
                          showSearch
                          style={{ width: "100%" }}
                          placeholder={t("selecione")}
                          defaultValue={est.hour_factory_id}
                          filterOption={filterOptions}
                          options={optionsHoursFactory}
                          onChange={
                            value =>
                              handleUpdateHourFactory({
                                hours_factory_id: value,
                                activity_id: est.requirement_id,
                              })
                            // ts-ignore
                          }
                        />
                      </>
                    )}
                </Flex>
              </ContainerFlex>
            </EstimationLine>
          );
        })}
        <TotalHoursContainer>
          Total:<TotalHours>{totalHours}h</TotalHours>
        </TotalHoursContainer>
        {!validatedEstimations && (
          <Tag color="error">
            Aguarde até todas os requisitos serem estimados
          </Tag>
        )}
      </Modal>
      <Button type="primary" onClick={handleShowModal}>
        {t("estimate")}
      </Button>
    </>
  );
};

export default EstimateModal;
